import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import useRequestersService from '../../services/useRequestersService';
import showToast from '../../utils/toast';
import '../Services/Adicional/AdicionalCatalogModal.css';

export interface QuickCreatedRequester {
  id: number;
  name: string;
  first_lastname: string;
  email?: string;
  company: number;
}

interface RequesterQuickAddModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: number;
  onCreated: (requester: QuickCreatedRequester) => void;
}

/**
 * Helper para crear un solicitante sin salir del formulario de servicios (paso 3).
 */
const RequesterQuickAddModal: React.FC<RequesterQuickAddModalProps> = ({
  isOpen,
  onClose,
  companyId,
  onCreated,
}) => {
  const { store, fetching } = useRequestersService();
  const [name, setName] = useState('');
  const [firstLastname, setFirstLastname] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const loading = Boolean(fetching.store);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setFirstLastname('');
      setPhoneNumber('');
      setEmail('');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !loading) onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, loading]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedLast = firstLastname.trim();
    if (!trimmedName || !trimmedLast || !companyId || loading) return;

    store(
      {
        company: companyId,
        name: trimmedName,
        first_lastname: trimmedLast,
        phone_number: phoneNumber.trim(),
        email: email.trim(),
      },
      {
        onSuccess: (r: any) => {
          const id = r?.data?.id;
          if (typeof id !== 'number') {
            showToast('El solicitante se creó pero no se pudo seleccionar automáticamente', 'info');
            onClose();
            return;
          }
          showToast('Solicitante creado correctamente', 'success');
          onCreated({
            id,
            name: String(r?.data?.name ?? trimmedName),
            first_lastname: String(r?.data?.first_lastname ?? trimmedLast),
            email: String(r?.data?.email ?? email.trim() ?? ''),
            company: Number(r?.data?.company ?? companyId),
          });
        },
        onError: (r: any) => {
          const errors = r?.errors ?? r?.data?.errors ?? r?.data;
          const firstFieldErr =
            typeof errors === 'object' && errors
              ? Object.values(errors).flat?.()?.[0] ??
                Object.values(errors)[0]
              : null;
          const msg =
            (typeof firstFieldErr === 'string' && firstFieldErr.trim()) ||
            (typeof r?.message === 'string' && r.message.trim()) ||
            'No se pudo crear el solicitante';
          showToast(String(msg), 'error');
        },
      }
    );
  };

  if (!isOpen) return null;

  return (
    <div className="adicional-modal-overlay" onClick={loading ? undefined : onClose}>
      <div className="adicional-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="adicional-modal-header">
          <h3 className="adicional-modal-title">Nuevo solicitante</h3>
          <button
            type="button"
            className="adicional-modal-close"
            onClick={onClose}
            disabled={loading}
            aria-label="Cerrar"
          >
            <X size={22} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="adicional-modal-form">
          <div className="adicional-modal-field">
            <label htmlFor="qa-req-name">Nombre</label>
            <input
              id="qa-req-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej. María"
              required
              autoFocus
              disabled={loading}
              maxLength={255}
            />
          </div>
          <div className="adicional-modal-field">
            <label htmlFor="qa-req-lastname">Apellido</label>
            <input
              id="qa-req-lastname"
              type="text"
              value={firstLastname}
              onChange={(e) => setFirstLastname(e.target.value)}
              placeholder="Ej. González"
              required
              disabled={loading}
              maxLength={255}
            />
          </div>
          <div className="adicional-modal-field">
            <label htmlFor="qa-req-phone">Teléfono (opcional)</label>
            <input
              id="qa-req-phone"
              type="text"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="Ej. +56 9 1234 5678"
              disabled={loading}
              maxLength={20}
            />
          </div>
          <div className="adicional-modal-field">
            <label htmlFor="qa-req-email">Correo (opcional)</label>
            <input
              id="qa-req-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Ej. maria@empresa.cl"
              disabled={loading}
              maxLength={255}
            />
          </div>
          <div className="adicional-modal-actions">
            <button
              type="button"
              className="adicional-modal-btn secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="adicional-modal-btn primary"
              disabled={loading || !name.trim() || !firstLastname.trim()}
            >
              {loading ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RequesterQuickAddModal;
